import { FC, Fragment, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { twMerge } from 'tailwind-merge';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import {
  DividendDistributionOverTime,
  DividendFrequency,
} from '../../../hooks/dividend/dividend.types';
import { DividendHooks } from '../../../hooks/index';
import { useScreenSize } from '../../../hooks/use-screen-size.hook';
import { getTranslation } from '../../../utils/get-translation.util';
import { formatDividendAmount } from '../utils/format-dividend-amount.util';

const MAX_AXIS_LABEL_CHARS = 12;

const truncateAxisLabel = (label: string): string =>
  label.length > MAX_AXIS_LABEL_CHARS ? `${label.slice(0, MAX_AXIS_LABEL_CHARS - 1)}…` : label;

const BAR_FILL = '#D1E3FF';
const BAR_STROKE = '#B4D1FE';
const BAR_ACTIVE_FILL = '#2565C8';
const BAR_ACTIVE_STROKE = '#2565C8';

type DistributionTooltipDatum = {
  label: string;
  /** Second line of the tooltip, e.g. "13 Payouts · 26 Stakeholders" — absent for Over Time. */
  meta?: string[];
  value: number;
};

const DistributionTooltip: FC<{
  active?: boolean;
  formatAmount: (value: number) => string;
  payload?: { payload: DistributionTooltipDatum }[];
}> = ({ active, formatAmount, payload }) => {
  if (!active || !payload?.length) return null;

  const { label, meta = [], value } = payload[0].payload;

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-white py-2 shadow-2xl">
      <div className="border-b border-gray-200 px-2 pb-2">
        <p className="text-label-sm font-semibold uppercase tracking-[1px] text-gray-700">
          {label}
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 px-2">
          <span className="text-label-sm font-semibold tracking-[1px] text-gray-700">
            <AppFormattedMessage id="TOTAL_ALL_TIME_PAYOUT" />
          </span>
          <span className="text-label-sm font-semibold tracking-[1px] text-forest-600">
            {formatAmount(value)}
          </span>
        </div>
        {meta.length > 0 && (
          <div className="flex h-4 items-center gap-2 px-2">
            {meta.map((entry, index) => (
              <Fragment key={entry}>
                {index > 0 && <span className="size-1 shrink-0 rounded-full bg-gray-400" />}
                <span className="text-label-sm font-medium tracking-[1px] text-gray-400">
                  {entry}
                </span>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Recharts spreads bars evenly whatever their count, so a fixed width leaves two share classes
// looking like threads on a wide chart and crowds a long list. Scale with the count instead.
const barSizeFor = (count: number): number => Math.round(Math.min(72, Math.max(24, 320 / count)));

// Shape only — these sit blurred behind the empty-state message, so the values mean nothing.
const PLACEHOLDER_BAR_DATA: DistributionTooltipDatum[] = [
  { label: '', value: 6 },
  { label: '', value: 9 },
  { label: '', value: 4 },
  { label: '', value: 7 },
  { label: '', value: 3 },
];

const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

enum ChartTab {
  BY_SHARE_CLASS = 'by-share-class',
  BY_STAKEHOLDER = 'by-stakeholder',
  OVER_TIME = 'over-time',
}

const TOP_BARS = 5;

// Past a handful of bars the labels collide and the small values flatten into the axis, so the
// tail is folded into a single "Other" bar (86cbb1gdr).
const foldTail = (
  bars: DistributionTooltipDatum[],
  countLabel: (count: number) => string,
): DistributionTooltipDatum[] => {
  if (bars.length <= TOP_BARS + 1) return bars;

  const tail = bars.slice(TOP_BARS);

  return [
    ...bars.slice(0, TOP_BARS),
    {
      label: getTranslation('OTHER'),
      meta: [countLabel(tail.length)],
      value: tail.reduce((sum, bar) => sum + bar.value, 0),
    },
  ];
};

const TAB_LABELS: Record<ChartTab, string> = {
  [ChartTab.BY_SHARE_CLASS]: getTranslation('BY_SHARE_CLASS'),
  [ChartTab.BY_STAKEHOLDER]: getTranslation('BY_STAKEHOLDER'),
  [ChartTab.OVER_TIME]: getTranslation('OVER_TIME'),
};

const periodLabel = (item: DividendDistributionOverTime): string => {
  if (item.frequency === DividendFrequency.ANNUALLY) return String(item.year);
  if (item.frequency === DividendFrequency.QUARTERLY)
    return `Q${item.periodIndex ?? ''} ${item.year}`;
  if (item.frequency === DividendFrequency.SEMI_ANNUALLY)
    return `H${item.periodIndex ?? ''} ${item.year}`;
  if (item.frequency === DividendFrequency.MONTHLY && item.periodIndex) {
    return `${MONTH_ABBR[item.periodIndex - 1]} ${item.year}`;
  }
  return String(item.year);
};

type Props = {
  companyId: string;
  currencySymbol: string;
};

export const DividendDistributionChart: FC<Props> = ({ companyId, currencySymbol }) => {
  const [activeTab, setActiveTab] = useState<ChartTab>(ChartTab.BY_SHARE_CLASS);
  const [activeBarIndex, setActiveBarIndex] = useState<null | number>(null);

  const { distribution, isLoading } = DividendHooks.useDividendDistribution({ companyId });

  const shareClassBarData = useMemo(
    () =>
      (distribution?.byShareClass ?? []).map((item) => ({
        label: item.shareClassName,
        meta: [
          `${item.payoutsCount} ${getTranslation(item.payoutsCount === 1 ? 'PAYOUT' : 'PAYOUTS')}`,
          `${item.stakeholdersCount} ${getTranslation(
            item.stakeholdersCount === 1 ? 'STAKEHOLDER' : 'STAKEHOLDERS',
          )}`,
        ],
        value: item.total,
      })),
    [distribution],
  );

  const stakeholderBarData = useMemo(
    () =>
      (distribution?.byStakeholder ?? []).map((item) => ({
        label: item.stakeholderName,
        meta: [
          `${item.payoutsCount} ${getTranslation(item.payoutsCount === 1 ? 'PAYOUT' : 'PAYOUTS')}`,
          `${item.shareClassesCount} ${getTranslation(
            item.shareClassesCount === 1 ? 'SHARE_CLASS' : 'SHARE_CLASSES',
          )}`,
        ],
        value: item.total,
      })),
    [distribution],
  );

  const overTimeBarData = useMemo(
    () =>
      (distribution?.overTime ?? []).map((item) => ({
        label: periodLabel(item),
        value: item.total,
      })),
    [distribution],
  );

  const formatAmount = (value: number) => `${currencySymbol}${formatDividendAmount(value)}`;

  const barDataMap: Record<ChartTab, DistributionTooltipDatum[]> = {
    [ChartTab.BY_SHARE_CLASS]: foldTail(
      shareClassBarData,
      (count) =>
        `${count} ${getTranslation(count === 1 ? 'SHARE_CLASS' : 'SHARE_CLASSES').toLowerCase()}`,
    ),
    [ChartTab.BY_STAKEHOLDER]: foldTail(
      stakeholderBarData,
      (count) =>
        `${count} ${getTranslation(count === 1 ? 'STAKEHOLDER' : 'STAKEHOLDERS').toLowerCase()}`,
    ),
    [ChartTab.OVER_TIME]: overTimeBarData,
  };

  const activeBarData = barDataMap[activeTab];
  const isEmpty = activeBarData.length === 0;
  // With nothing to plot there is nothing to blur, so the empty state gets bars of its own.
  const chartData = isEmpty ? PLACEHOLDER_BAR_DATA : activeBarData;

  const { width } = useScreenSize();
  const isPhone = width < 460;

  return (
    <div className="flex w-full flex-1 flex-col gap-3 rounded-lg bg-gray-50 px-1 pb-1 pt-3">
      <div className="flex h-9 items-center justify-between px-4 max-xs:h-auto max-xs:flex-col max-xs:items-stretch max-xs:gap-3 max-xs:pb-1">
        <span className="text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
          <AppFormattedMessage id="DIVIDEND_DISTRIBUTION" />
        </span>
        <div className="flex overflow-hidden rounded border border-gray-200 max-xs:w-full">
          {[ChartTab.BY_SHARE_CLASS, ChartTab.BY_STAKEHOLDER].map((tab, index) => (
            <button
              className={twMerge(
                'whitespace-nowrap px-3 py-2 text-xs transition-colors max-xs:flex-1 max-xs:text-center',
                index > 0 && 'border-l border-gray-200',
                activeTab === tab
                  ? 'bg-brand-25 font-[550] text-gray-700'
                  : 'bg-white font-normal text-[#858593]',
              )}
              key={tab}
              onClick={() => setActiveTab(tab)}
              type="button"
            >
              {TAB_LABELS[tab]}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-1 flex-col rounded-md bg-white p-3 shadow-sm">
        {isLoading ? (
          <div className="flex min-h-[240px] flex-1 items-center justify-center text-sm text-gray-400">
            <AppFormattedMessage id="LOADING" />
          </div>
        ) : (
          // Empty state keeps the chart underneath, blurred, with the message on top — same shape
          // as the Equity & Capital Change graph in My Holdings.
          <div
            className={twMerge(
              'relative flex items-center justify-center',
              isPhone ? 'w-full' : 'min-h-[240px] flex-1',
            )}
            style={isPhone ? { height: Math.max(chartData.length * 48 + 48, 140) } : undefined}
          >
            {isEmpty && (
              <span className="absolute z-1 max-w-[280px] px-4 text-center text-label-sm font-medium text-gray-500">
                <AppFormattedMessage id="DIVIDEND_DISTRIBUTION_EMPTY" />
              </span>
            )}
            <ResponsiveContainer
              className={twMerge(isEmpty && 'pointer-events-none select-none blur-md')}
              height="100%"
              width="100%"
            >
              <BarChart
                barSize={isPhone ? 32 : barSizeFor(chartData.length)}
                data={chartData}
                layout={isPhone ? 'vertical' : 'horizontal'}
                margin={{ bottom: 0, left: 0, right: 0, top: 4 }}
                onMouseLeave={() => setActiveBarIndex(null)}
                onMouseMove={(state: { activeTooltipIndex?: number }) =>
                  setActiveBarIndex(state?.activeTooltipIndex ?? null)
                }
              >
                <CartesianGrid horizontal={!isPhone} stroke="#EAECF0" vertical={isPhone} />
                {isPhone ? (
                  <XAxis
                    axisLine={false}
                    style={{ fontSize: 10 }}
                    tick={{ fill: '#475467' }}
                    tickFormatter={(v: number) => `${currencySymbol}${formatDividendAmount(v)}`}
                    tickLine={false}
                    type="number"
                  />
                ) : (
                  <XAxis
                    axisLine={false}
                    dataKey="label"
                    interval={0}
                    style={{ fontSize: 11 }}
                    tick={{ fill: '#667085' }}
                    tickFormatter={truncateAxisLabel}
                    tickLine={false}
                  />
                )}
                {isPhone ? (
                  <YAxis
                    axisLine={false}
                    dataKey="label"
                    interval={0}
                    style={{ fontSize: 10 }}
                    tick={{ fill: '#475467' }}
                    tickFormatter={truncateAxisLabel}
                    tickLine={false}
                    type="category"
                    width={80}
                  />
                ) : (
                  <YAxis
                    axisLine={false}
                    style={{ fontSize: 11 }}
                    tick={{ fill: '#667085' }}
                    tickFormatter={(v: number) => `${currencySymbol}${formatDividendAmount(v)}`}
                    tickLine={false}
                    width={72}
                  />
                )}
                <Tooltip
                  content={<DistributionTooltip formatAmount={formatAmount} />}
                  cursor={{ fill: 'transparent' }}
                />
                <Bar dataKey="value" radius={isPhone ? [0, 4, 4, 0] : [4, 4, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell
                      fill={index === activeBarIndex ? BAR_ACTIVE_FILL : BAR_FILL}
                      key={entry.label}
                      stroke={index === activeBarIndex ? BAR_ACTIVE_STROKE : BAR_STROKE}
                      style={{ transition: 'fill 0.25s ease, stroke 0.25s ease' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
